import { useMemo } from 'react';
import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

import APIError from 'types/api_error';
import { LumeoFileWithSearchMeta } from 'types/file_with_search_meta';
import {
  CursorPaginated,
  OffsetPaginated,
  OffsetPaginationParams,
} from 'types/pagination';

import { useFiles, UseFilesCursorParams, useFilesCursor, UseFilesParams } from './useFiles';
import { useFilesSearch } from './useFilesSearch';

type UseFilesWithSearchMetadataParams = {
  include_search_metadata?: boolean | undefined;
};

type UseFilesWithSearchMetadataOptions = UseQueryOptions<
  OffsetPaginated<LumeoFileWithSearchMeta>,
  APIError
> & {
  params?: UseFilesParams &
    OffsetPaginationParams &
    UseFilesWithSearchMetadataParams;
};

type UseFilesCursorWithSearchMetadataOptions = UseQueryOptions<
  CursorPaginated<LumeoFileWithSearchMeta>,
  APIError
> & {
  params?: UseFilesCursorParams & UseFilesWithSearchMetadataParams;
};

type UseFilesWithSearchMetadataResult<TData> = UseQueryResult<TData, APIError> & {
  isSearchMetadataLoading: boolean;
};

/**
 * Hook that fetches files and optionally enriches them with search metadata
 * (events, objects, texts) when include_search_metadata is true
 */
export function useFilesWithSearchMetadata({
  params,
  ...options
}: UseFilesWithSearchMetadataOptions = {}): UseFilesWithSearchMetadataResult<
  OffsetPaginated<LumeoFileWithSearchMeta>
> {
  const includeSearchMetadata = params?.include_search_metadata === true;

  // Fetch files with the base useFiles hook
  const filesQuery = useFiles({
    params,
    ...options,
  });

  // Only fetch search metadata if requested and files are loaded
  const hasFiles = (filesQuery.data?.data.length ?? 0) > 0;
  const shouldFetchSearch =
    includeSearchMetadata && filesQuery.isSuccess && hasFiles;

  // Get file IDs for search query
  const fileIds = useMemo(() => {
    if (!shouldFetchSearch || !filesQuery.data?.data) {
      return [];
    }

    return filesQuery.data.data.map((file) => file.id);
  }, [shouldFetchSearch, filesQuery.data?.data]);

  // Fetch search metadata for all files at once
  const searchQuery = useFilesSearch({
    params: {
      file_ids: fileIds,
      limit: fileIds.length || 50, // Fetch all files in one request
    },
    enabled: shouldFetchSearch && fileIds.length > 0,
    queryKey: ['files-search-metadata', fileIds],
  });

  const isSearchMetadataFetching = shouldFetchSearch && searchQuery.isFetching;
  const isSearchMetadataLoading =
    shouldFetchSearch &&
    (searchQuery.isLoading || (searchQuery.isFetching && !searchQuery.data));

  // Merge search metadata into files
  const enrichedData = useMemo(() => {
    if (!includeSearchMetadata || !filesQuery.data) {
      return filesQuery.data;
    }

    if (!searchQuery.data?.data) {
      return filesQuery.data;
    }

    // Create a map of search results by file_id for efficient lookup
    const searchMetadataMap = new Map(
      searchQuery.data.data.map((searchResult) => [
        searchResult.file_id,
        {
          events: searchResult.events,
          objects: searchResult.objects,
          texts: searchResult.texts,
          trackables: searchResult.trackables,
          search_score: searchResult.search_score,
        },
      ])
    );

    // Enrich files with search metadata
    const enrichedFiles = filesQuery.data.data.map((file) => {
      const metadata = searchMetadataMap.get(file.id);
      if (metadata) {
        return {
          ...file,
          events: metadata.events,
          objects: metadata.objects,
          texts: metadata.texts,
          trackables: metadata.trackables,
          search_score: metadata.search_score,
        };
      }
      return file;
    });

    return {
      ...filesQuery.data,
      data: enrichedFiles,
    };
  }, [includeSearchMetadata, filesQuery.data, searchQuery.data]);

  // Return the files query with enriched data
  return {
    ...filesQuery,
    data: enrichedData as OffsetPaginated<LumeoFileWithSearchMeta> | undefined,
    isSearchMetadataLoading,
    // Search metadata should not block rendering the base files list.
    isLoading: filesQuery.isLoading,
    isFetching: filesQuery.isFetching || isSearchMetadataFetching,
  } as UseFilesWithSearchMetadataResult<OffsetPaginated<LumeoFileWithSearchMeta>>;
}

/**
 * Cursor variant of useFilesWithSearchMetadata that fetches files first and
 * then enriches that page with search metadata in a separate query.
 */
export function useFilesCursorWithSearchMetadata({
  params,
  ...options
}: UseFilesCursorWithSearchMetadataOptions = {}): UseFilesWithSearchMetadataResult<
  CursorPaginated<LumeoFileWithSearchMeta>
> {
  const includeSearchMetadata = params?.include_search_metadata === true;

  const filesQuery = useFilesCursor({
    params,
    ...options,
  });

  const hasFiles = (filesQuery.data?.data.length ?? 0) > 0;
  const shouldFetchSearch =
    includeSearchMetadata && filesQuery.isSuccess && hasFiles;

  const fileIds = useMemo(() => {
    if (!shouldFetchSearch || !filesQuery.data?.data) {
      return [];
    }

    return filesQuery.data.data.map((file) => file.id);
  }, [shouldFetchSearch, filesQuery.data?.data]);

  const searchQuery = useFilesSearch({
    params: {
      file_ids: fileIds,
      limit: fileIds.length || 50,
    },
    enabled: shouldFetchSearch && fileIds.length > 0,
    queryKey: ['files-search-metadata', fileIds],
  });

  const isSearchMetadataFetching = shouldFetchSearch && searchQuery.isFetching;
  const isSearchMetadataLoading =
    shouldFetchSearch &&
    (searchQuery.isLoading || (searchQuery.isFetching && !searchQuery.data));

  const enrichedData = useMemo(() => {
    if (!includeSearchMetadata || !filesQuery.data) {
      return filesQuery.data;
    }

    if (!searchQuery.data?.data) {
      return filesQuery.data;
    }

    const searchMetadataMap = new Map(
      searchQuery.data.data.map((searchResult) => [
        searchResult.file_id,
        {
          events: searchResult.events,
          objects: searchResult.objects,
          texts: searchResult.texts,
          trackables: searchResult.trackables,
          search_score: searchResult.search_score,
        },
      ])
    );

    const enrichedFiles = filesQuery.data.data.map((file) => {
      const metadata = searchMetadataMap.get(file.id);
      if (metadata) {
        return {
          ...file,
          events: metadata.events,
          objects: metadata.objects,
          texts: metadata.texts,
          trackables: metadata.trackables,
          search_score: metadata.search_score,
        };
      }
      return file;
    });

    return {
      ...filesQuery.data,
      data: enrichedFiles,
    };
  }, [includeSearchMetadata, filesQuery.data, searchQuery.data]);

  return {
    ...filesQuery,
    data: enrichedData as CursorPaginated<LumeoFileWithSearchMeta> | undefined,
    isSearchMetadataLoading,
    // Search metadata should not block rendering the base files list.
    isLoading: filesQuery.isLoading,
    isFetching: filesQuery.isFetching || isSearchMetadataFetching,
  } as UseFilesWithSearchMetadataResult<CursorPaginated<LumeoFileWithSearchMeta>>;
}
